import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Location } from '@angular/common';
import { AuthValidator } from '../libraries/auth-validator/auth-validator';
import { ProfileService } from '../services/profile/profile.service';
import { Constants } from '../constants';
import { SharedDataService } from '../services/shared-data/shared-data.service';
import { filter, Observable, switchMap } from 'rxjs';
import { RaInjectOneidAuthTokenService } from '@wdpr/ra-angular-oneid-auth-token-injector';
import { ConfigService } from '../services/config/config.service';

/**
 * Route guard: allows navigation only after profile-authenticator readiness and
 * OneID-backed guest login / profile validation succeed.
 *
 * Legacy vs Universal Auth is determined at bootstrap (e.g. `isUniversalAuthEnabled`
 * in {@link ConfigService} and Universal initializer in AppModule), not by a separate
 * cookie-verification branch here. This guard always follows one path: wait for
 * `profileAuthInitialized` on {@link SharedDataService}, then token init and
 * {@link AuthValidator} / {@link ProfileService} as in `_getProfileWithProfileAuthenticator()`.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  constructor(
    private _profileService: ProfileService,
    private _authValidator: AuthValidator,
    private _sharedDataService: SharedDataService,
    private _raInjectOneidAuthTokenService: RaInjectOneidAuthTokenService,
    private _configService: ConfigService,
    private _location: Location
  ) {}

  /**
   * Guards the route: sets referrer when appropriate, then runs the single
   * profile-authenticator flow via `_getProfileWithProfileAuthenticator()` (wait for
   * `profileAuthInitialized`, OneID token init, then validate login and load profile with swid).
   *
   * @returns Observable or Promise that resolves to true if access is allowed, or redirects
   *   to login/homepage when auth fails.
   */
  canActivate(): any {
    this._setReferrer();
  
    return this._getProfileWithProfileAuthenticator();
  }


  /**
   * 
   * @desc Get Profile with profile authenticator
   * 
   * Universal Auth (OneID V4/V5) path: wait on profileAuthInitialized, then token init and profile validation.
   *
   * Unblocked when SharedDataService.profileAuthInitialized becomes defined (true).
   * AppModule APP_INITIALIZER registers 'authenticatorUniversalReady' before ClientIntegration.AngularInit.runNow
   * and calls updateValue('profileAuthInitialized', true) on that event and again when runNow resolves.
   * AppComponent also listens for 'authenticatorUniversalReady' (and removes on destroy) and sets the same flag;
   * it may set it via document 'navigation' oneId-init as well. Requires isUniversalAuthEnabled
   * in loaded config or the initializer never sets the flag and this observable stays pending.
   * 
   * @returns {Observable<boolean>} Observable that resolves to true if access is allowed, or false if access is denied.
   * 
   */
  private _getProfileWithProfileAuthenticator() {

    return this._sharedDataService.getValue('profileAuthInitialized')
      .pipe(
        filter((value) => {
          return value !== undefined;
        }),
        switchMap((profileAuth: any) => {

          if (profileAuth) {

            return this._authTokenInit().then(() => {

              return this._authValidator.handleGuestLogin().then(result => {
                if (result) {
                  // if login is successful but getting profile fails, then redirect to login page for page reset
                  return this._profileService
                    .load(result)
                    .then((profile) => {
                      return profile && profile.swid ? true : this._redirectToLogin();
                    })
                    .catch((err) => {
                      return this._redirectToLogin();
                    });
                // if login modal was closed, then go to home page (current login page behavior)
                } else {
                  return this._redirectToHomepage();
                }
              });
            });
          // profile auth doesn't exist, redirect to login
          } else {
            return this._redirectToLogin();
          }
        })
      );
  }


  /**
   * @desc Initialize RA Lib for Auth Token handle
   * 
   * @returns {Promise<void>}
   */
  private _authTokenInit() {
    return this._raInjectOneidAuthTokenService.init({
      ENABLED: true,
      NONLOGGEDINFLOW: false,
      LOGGEDINFLOW: true
    });
  }

  /**
   * @desc Set referrer into Local Storage before redirect to the next route.
   * 
   * @returns {void}
   */
  private _setReferrer() {
    const currentRoute = this._location.path().replace('/', '');

    if (
      !!document.referrer &&
      document.referrer.indexOf('login') === -1 &&
      document.referrer.indexOf('queue') === -1 &&
      !currentRoute
    ) {
      localStorage.setItem('claimReferrer', document.referrer);
    }
  }

  /**
   * @desc Redirect to login screen
   * 
   *
   * @returns {Promise<unknown>}
   */
  private _redirectToLogin(): Promise<unknown> {
    window.location.href = window.location.origin + Constants.REDIRECT_LOGIN_PATH;

    return new Promise((resolve, reject) => { });
  }

 /**
   * @desc Redirect to home screen
   * 
   * @returns {Promise<unknown>}
   *
   */
   private _redirectToHomepage(): Promise<unknown> {
    window.location.href = window.location.origin;

    return new Promise((resolve, reject) => { });
  }

}
